import type { LineItem, Tuple } from "@/interfaces/estimate-detail";

export const getConfidence = (lineItem: LineItem, tuple: Tuple) => {
  function getNumericalConfidence(lineItem: LineItem): number | null {
    let rightConfidence = null;
    let leftConfidence = null;
    let lowConfidenceArrayValue = null;

    if (lineItem.rightMapping && lineItem.rightMapping.length > 0) {
      rightConfidence = lineItem.rightMapping[0].confidence * 100;
    }

    if (lineItem.leftMapping && lineItem.leftMapping.length > 0) {
      leftConfidence = lineItem.leftMapping[0].confidence * 100;
    }

    if (tuple.lowConfidenceArray && tuple.lowConfidenceArray.length > 0) {
      lowConfidenceArrayValue = Math.min(...tuple.lowConfidenceArray.map((val) => val.confidence * 100));
    }

    const confidenceValues = [rightConfidence, leftConfidence, lowConfidenceArrayValue];
    return confidenceValues.every((val) => val == null)
      ? null
      : Math.min(rightConfidence ?? 90, leftConfidence ?? 90, lowConfidenceArrayValue ?? 90);
  }

  if (lineItem.confirmed) {
    return "100%";
  }

  const c = lineItem.confidence;
  if (c != null) {
    if (c > 1) {
      const parentConf = tuple.lineItems[0]?.confidence;
      if (parentConf != null) {
        return `${(c * parentConf).toFixed(0)}%`;
      }
    }

    return `${(c * 100).toFixed(0)}%`;
  }

  // For complements, return (parent's confidence - 10%)
  if (lineItem.isComplement && !tuple.lineItems[0]?.isComplement) {
    const parentConfidence: number = parseFloat(getConfidence(tuple.lineItems[0], tuple) ?? "90") ?? 90;
    return `${(parentConfidence - 10).toFixed(0)}%`;
  }

  // If tuple had an error in the past but now doesn't, it was manually resolved.
  if (tuple.hadErrorThatHasBeenFixed === true) {
    return "100%";
  }

  // Line Item with issue that's not a low confidence scan
  const confidence = getNumericalConfidence(lineItem);
  if (tuple.hasError) {
    // If confidence greater than 90, it's most likely a "background problem", so we'll call it N/A for right now.
    if (confidence == null || confidence >= 90) {
      return "0%";
    }
  }

  // Manually added line item
  if (confidence == null) {
    return "100%";
  }

  // Cap Confidence of the handwriting scan at 90%.
  // Gets to 100% by being confirmed or manually editted/added.
  const capOCRConfidence = confidence > 90 ? 90 : confidence < 40 ? 40 : confidence;
  return `${capOCRConfidence.toFixed(0)}%`;
};
